import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { pageIds } from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';

import { Notification } from '../../../../dashboard/interfaces/notifications.interfaces';

import {
  useDeleteNotificationMutation,
  User_Notification_Status_Enum,
  useUpdateInAppNotificationMutation,
} from 'graphql/generated/hasura';
import ButtonComponent from 'components/button/buttonComponent';
import { replaceTokensToDate, sanitize } from 'utilities/sanitizer';
import { useHandleExtraActions } from 'hooks/useHandleExtraActions';
import { AlertExtraActions } from 'utilities/interfaces';

import OptionsModal from 'components/optionsModal/OptionsModal';
import { ReactComponent as TrashIcon } from 'assets/icons/trashIcon.svg';
import { ReactComponent as BlueCheckActive } from 'assets/icons/checkActive.svg';
import { ProfileSectionTypes } from '../../../../my-account/pages/my-profile/enums';

interface INotificationItem extends Notification {
  refreshNotification?: () => void;
}

export function MobileNotificationItem({
  title,
  date,
  id,
  isRead,
  extraActions,
  constants,
  message,
  action,
  onClick,
  refreshNotification,
}: INotificationItem) {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.NOTIFICATIONS,
  });

  const [deleteNotificationMutation, { loading: deleteLoading }] =
    useDeleteNotificationMutation({
      onCompleted: () => refreshNotification && refreshNotification(),
    });

  const navigate = useNavigate();

  const [opened, setOpened] = useState<boolean>(false);

  const titles = useMemo(() => {
    if (!constants) {
      return title;
    }
    return replaceTokensToDate(title, constants);
  }, [constants, title]);

  const messages = useMemo(() => {
    if (!constants) {
      return message;
    }
    return replaceTokensToDate(message, constants);
  }, [constants, message]);

  const { handler: customActionsHandler, isLoading } = useHandleExtraActions();

  const [
    updateInAppNotificationMutation,
    { loading: updateNotificationLoading },
  ] = useUpdateInAppNotificationMutation({
    onCompleted: () => {
      refreshNotification && refreshNotification();
    },
  });

  const markAsRead = useCallback(
    async (notificationId: string) => {
      const response = await updateInAppNotificationMutation({
        variables: {
          id: String(notificationId),
          status: User_Notification_Status_Enum.Read,
        },
      });

      setOpened(false);
      if (!response.data) {
        throw new Error('Failed to update notification');
      }
    },
    [updateInAppNotificationMutation],
  );

  const deleteNotification = useCallback(
    async (id: string) => {
      await deleteNotificationMutation({
        variables: {
          notificationId: id,
        },
      });
    },
    [deleteNotificationMutation],
  );

  const handleOnClick = async (
    notificationId: string,
    route?: string,
    message = '',
  ) => {
    try {
      await markAsRead(notificationId);

      if (!route) return;

      if (
        message
          .toLocaleUpperCase()
          .includes(ProfileSectionTypes.PROFILE.toLocaleUpperCase())
      ) {
        return navigate(route, {
          state: { section: ProfileSectionTypes.PROFILE },
        });
      }
      if (
        message
          .toLocaleUpperCase()
          .includes(ProfileSectionTypes.CONTACT.toLocaleUpperCase())
      ) {
        return navigate(route, {
          state: { section: ProfileSectionTypes.CONTACT },
        });
      }
      return navigate(route);
    } catch (error: unknown) {
      console.log(error);
      throw new Error('Failed to update notification');
    }
  };

  const options = useMemo(
    () => [
      {
        Icon: BlueCheckActive,
        label: locale?.markAsRead || 'Mark as read',
        hidden: isRead,
        disabled: updateNotificationLoading || deleteLoading,
        iconClassName: 'w-5 h-5',
        onClick: () => markAsRead(id),
      },
      {
        Icon: TrashIcon,
        disabled: updateNotificationLoading || deleteLoading,
        label: locale?.deleteNotification || ' Delete notification',
        iconClassName: 'stroke-clc-blue',
        onClick: () => deleteNotification(id),
      },
    ],
    [
      deleteLoading,
      updateNotificationLoading,
      deleteNotification,
      id,
      isRead,
      locale?.deleteNotification,
      locale?.markAsRead,
      markAsRead,
    ],
  );

  const handleAction = async (action: AlertExtraActions) => {
    const updateNotificationVariable = {
      id: String(id),
      status: User_Notification_Status_Enum.Read,
    };

    try {
      if (action.customAction && customActionsHandler[action.customAction]) {
        const [response] = await Promise.all([
          updateInAppNotificationMutation({
            variables: updateNotificationVariable,
          }),
          customActionsHandler[action.customAction](
            action.customActionParams ?? {},
            false,
          ),
        ]);
        if (!response.data) {
          throw new Error('Failed to update notification');
        }
        onClick && onClick();
      } else {
        navigate((action?.route || '') ?? -1);

        const response = await updateInAppNotificationMutation({
          variables: updateNotificationVariable,
        });
        onClick && onClick();
        if (!response.data) {
          throw new Error('Failed to update notification');
        }
      }
    } catch (error: unknown) {
      throw new Error('Failed to update notification');
    }
    return;
  };

  return (
    <div
      className={twMerge(
        'flex relative flex-col gap-2.5 px-[30px] py-4  bg-white',
        isRead && 'bg-neutral-100',
      )}
      data-testid={'notification-item'}
      data-read-status={isRead}
    >
      <div className="flex relative items-start gap-2">
        <span
          className={twMerge(
            'w-2 h-2 absolute rounded-full bg-clc-blue -left-4 top-2',
            isRead && 'hidden',
          )}
        />
        <p
          className={twMerge(
            'text-xs py-1 text-med-gray-3 font-semibold whitespace-nowrap',
          )}
        >
          {date}
        </p>

        <div className="ml-auto relative -top-1">
          <OptionsModal
            opened={opened}
            setOpened={setOpened}
            options={options}
            className="!bg-transparent !border-transparent -mr-5"
            optionsMenuClassName="rounded-md w-[16rem] border-[#00000026] -left-[14rem]"
            optionsClassName="uppercase text-clc-blue text-sm font-bold underline"
            btnTitleClassName="hidden"
          />
        </div>
      </div>

      <div
        onClick={() => action?.route && handleOnClick(id, action?.route, title)}
        className={twMerge(
          'text-neutral-800 text-sm font-semibold notification-item',
          isRead && 'text-med-gray-3',
          action?.route ? 'cursor-pointer' : 'cursor-auto',
        )}
      >
        <span dangerouslySetInnerHTML={{ __html: sanitize(titles) }} />
        {!!titles && ' '}
        <span dangerouslySetInnerHTML={{ __html: sanitize(messages) }} />
      </div>

      <div className={twMerge('flex flex-col desktop:flex-row  gap-1.5')}>
        {[action, ...(extraActions || [])]?.map((extraAction, index) =>
          extraAction?.actionName ? (
            <ButtonComponent
              type={extraAction?.type ?? 'outlined'}
              key={index}
              testID={`${extraAction?.type}-${id}`}
              onClick={() => extraAction && handleAction(extraAction)}
              disabled={isLoading || updateNotificationLoading || deleteLoading}
              className="!text-xs whitespace-nowrap"
              fullWidthClassName="desktop:w-auto w-full"
            >
              {extraAction?.actionName}
            </ButtonComponent>
          ) : null,
        )}
      </div>
    </div>
  );
}
